import React from 'react';
import { FacetItem, SearchFacet, SearchTabItem, BaselineFacetItem, BaselineFacetMap } from '../../types/search';

export const buildQueryString = (
  searchTerm: string,
  tabs: SearchTabItem[],
  sortOrder: number,
  filters: SearchFacet[],
  currentPage: number
): string => {
  let query = `?q=${encodeURIComponent(searchTerm)}`;
  query += `&page=${currentPage}`;

  const selectedTab = tabs.find(tab => tab.isSelected);
  if (selectedTab) {
    query += `&pagetype=${encodeURIComponent(selectedTab.key)}`;
  }

  query += `&order=${sortOrder}`;
  const facetParams = filters.reduce((acc, facet) => {
    const selectedItems = facet.items.filter(item => item.isSelected).map(item => item.value);
    if (selectedItems.length > 0) {
      acc.push(`${facet.key}=${selectedItems.join(',')}`);
    }
    return acc;
  }, [] as string[]);

  if (facetParams.length) {
    query += `&${facetParams.join('&')}`;
  }

  return query;
};

export const getPendingSelectionsFromUrl = (): Record<string, string[]> => {
  const params = new URLSearchParams(window.location.search);
  const pendingSelections: Record<string, string[]> = {};

  params.forEach((value, key) => {
    if (!['q', 'page', 'order', 'pagetype'].includes(key)) {
      pendingSelections[key] = value.split(',');
    }
  });

  return pendingSelections;
};

export const applyPendingSelectionsToFacets = (
  facets: SearchFacet[],
  pendingSelections: Record<string, string[]>
): SearchFacet[] => {
  return facets.map(facet => ({
    ...facet,
    items: facet.items.map(item => ({
      ...item,
      isSelected: pendingSelections[facet.key]?.includes(item.value) || false,
    })),
  }));
};

export const updateFacetsSelection = (facets: SearchFacet[], selections: Record<string, string[]>): SearchFacet[] => {
  return facets.map(facet => ({
    ...facet,
    items: facet.items.map(item => ({
      ...item,
      isSelected: selections[facet.key]?.includes(item.value) || false,
    })),
  }));
};

export const updateFacetForRemoval = (facets: SearchFacet[], facetKey: string, itemValue: string): SearchFacet[] => {
  return facets.map(facet => {
    if (facet.key === facetKey) {
      return {
        ...facet,
        items: facet.items.map(item => (item.value === itemValue ? { ...item, isSelected: false } : item)),
      };
    }
    return facet;
  });
};

export const getSelectedFilters = (appliedFacets: SearchFacet[]) =>
  appliedFacets.flatMap(facet =>
    facet.items
      .filter(item => item.isSelected)
      .map(item => ({
        facetKey: facet.key,
        label: item.label,
        value: item.value,
      }))
  );

export const renderOpenFacetItems = (
  openFacet: SearchFacet,
  pendingSelections: Record<string, string[]>,
  onFacetItemToggle: (facetKey: string, itemValue: string) => void
) =>
  openFacet.items.map(item => {
    const isSelected = pendingSelections[openFacet.key]?.includes(item.value);
    const inputId = `facet-${openFacet.key}-${item.value}`;

    return (
      <li
        key={item.value}
        className={isSelected ? 'search__filters-filter search__filters-filter--selected' : 'search__filters-filter'}
      >
        <label htmlFor={inputId} className="search__filters-filter-label">
          <input
            id={inputId}
            type="checkbox"
            checked={isSelected || false}
            disabled={item.total === 0 || item?.hide}
            onChange={() => onFacetItemToggle(openFacet.key, item.value)}
            className="search__filters-checkbox"
          />
          <span className="custom-checkbox" aria-hidden="true" />
          <span>{item.label}</span>
        </label>
      </li>
    );
  });

export const getValidFacetValues = (facet: SearchFacet, values: string[]): string[] => {
  return values.filter(v => {
    const matchingItem = facet.items.find(item => item.value === v);
    return matchingItem !== undefined && matchingItem.total > 0;
  });
};

export const updateFacetItemsForKey = (facet: SearchFacet, validValues: string[]): SearchFacet => ({
  ...facet,
  items: facet.items.map(item => ({
    ...item,
    isSelected: validValues.includes(item.value),
  })),
});

export const updateFacetsForUrlParam = (
  facetsArray: SearchFacet[],
  key: string,
  valuesFromUrl: string[]
): SearchFacet[] =>
  facetsArray.map(facet => {
    if (facet.key !== key) return facet;
    const validValues = valuesFromUrl.filter(v => {
      const matchingItem = facet.items.find(item => item.value === v);
      return matchingItem !== undefined && matchingItem.total > 0;
    });
    return updateFacetItemsForKey(facet, validValues);
  });

export const updatePendingSelections = (
  prevSelections: Record<string, string[]>,
  newFacets: SearchFacet[],
  key: string,
  valuesFromUrl: string[]
): Record<string, string[]> => {
  const facet = newFacets.find(f => f.key === key);
  if (!facet) return prevSelections;
  const validValues = getValidFacetValues(facet, valuesFromUrl);
  return { ...prevSelections, [key]: validValues };
};

export const clearFacetItem = (item: FacetItem): FacetItem => ({
  ...item,
  isSelected: false,
});

export const clearFacet = (facet: SearchFacet): SearchFacet => ({
  ...facet,
  items: facet.items.map(clearFacetItem),
});

export const buildBaselineFacetMap = (facets: SearchFacet[] = []): BaselineFacetMap => {
  const map: BaselineFacetMap = {};
  for (const facet of facets) {
    map[facet.key] = (facet.items ?? []).map(
      (i: SearchFacet['items'][number]): BaselineFacetItem => ({
        value: String(i.value),
        label: i.label,
        pagetype: (i as any).pagetype,
      })
    );
  }
  return map;
};
